/**
 * @fileoverview `<LiquidationNumber>` — affichage d'un numéro LIQ.
 *
 * Les numéros de liquidation identifient les contraventions à régler. Ils
 * doivent être lisibles, copiables et visuellement distincts du texte courant.
 */

import { CopyButton } from './copy-button.tsx';
import { cn } from '../lib/utils.ts';

export interface LiquidationNumberProps {
  /** Numéro de liquidation, ex. `LIQ-921555`. */
  value: string;
  /** Affiche le bouton de copie. */
  copyable?: boolean;
  className?: string;
}

/**
 * Affichage monospace d'un numéro LIQ.
 *
 * @param props - Valeur LIQ, option copie et classe CSS.
 * @returns Bloc inline avec valeur monospace et bouton de copie optionnel.
 *
 * @example
 * ```tsx
 * <LiquidationNumber value="LIQ-921555" copyable />
 * ```
 */
export function LiquidationNumber({
  value,
  copyable = false,
  className,
}: LiquidationNumberProps): React.JSX.Element {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <code className="bg-muted text-quipux-navy-950 rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold">
        {value}
      </code>
      {copyable && <CopyButton value={value} ariaLabel="Copier le numéro de liquidation" />}
    </span>
  );
}
